import qrcode from "qrcode-generator";
import { button, element, errorMessage, message } from "./ui";

export function qrImage(value: string, label: string): SVGSVGElement {
  const code = qrcode(0, "M");
  code.addData(value);
  code.make();
  const size = code.getModuleCount();
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.classList.add("qr-code");
  svg.setAttribute("viewBox", `-4 -4 ${size + 8} ${size + 8}`);
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", label);
  const path = document.createElementNS(svg.namespaceURI, "path");
  let data = "";
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      if (code.isDark(y, x)) data += `M${x},${y}h1v1h-1z`;
    }
  path.setAttribute("d", data);
  svg.append(path);
  return svg;
}

interface NativeDetector {
  detect(source: CanvasImageSource): Promise<readonly { readonly rawValue: string }[]>;
}
type DetectorConstructor = new (options: { formats: string[] }) => NativeDetector;

/** Camera frames use the browser detector where available and bundled jsQR everywhere else. */
export class QrScanner {
  public readonly element = element("section", "scanner");
  private readonly video = element("video", "scanner__video");
  private readonly error = errorMessage();
  private stream: MediaStream | null = null;
  private generation = 0;
  private active = true;
  private scanning = false;
  private frame: number | null = null;
  private lastScan = 0;
  private detector: NativeDetector | null = null;
  private readonly canvas = document.createElement("canvas");
  private readonly context = this.canvas.getContext("2d", { willReadFrequently: true });

  public constructor(
    private readonly accepts: (value: string) => boolean,
    private readonly onCode: (value: string) => void,
  ) {
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.autoplay = true;
    this.video.setAttribute("aria-label", "QR code camera preview");
    const retry = button("Try camera again", "button button--secondary", () => this.start());
    const file = element("input");
    file.type = "file";
    file.accept = "image/*";
    file.addEventListener("change", () => {
      const image = file.files?.[0];
      if (image) void this.scanImage(image);
      file.value = "";
    });
    this.element.append(
      this.video,
      element("p", "muted", "Point your camera at the QR code."),
      this.error,
      retry,
      element("label", "field", "Or scan a saved QR image", file),
    );
    try {
      const Detector = (globalThis as unknown as { BarcodeDetector?: DetectorConstructor })
        .BarcodeDetector;
      if (Detector) this.detector = new Detector({ formats: ["qr_code"] });
    } catch {
      this.detector = null;
    }
    void this.start();
  }

  private async start(): Promise<void> {
    this.stopCamera();
    const generation = ++this.generation;
    this.scanning = false;
    this.error.textContent = "";
    try {
      if (!navigator.mediaDevices?.getUserMedia)
        throw new Error(
          "Camera access requires HTTPS and a browser with camera support. You can scan a saved QR image instead.",
        );
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      });
      if (!this.active || generation !== this.generation) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      this.stream = stream;
      this.video.srcObject = stream;
      await this.video.play();
      if (!this.active || generation !== this.generation) return;
      this.frame = requestAnimationFrame((time) => void this.tick(time, generation));
    } catch (failure) {
      if (this.active && generation === this.generation) {
        this.stopCamera();
        this.error.textContent = `Camera unavailable. Allow camera access in your browser settings, then try again. ${message(failure)}`;
      }
    }
  }
  private async decode(
    source: CanvasImageSource,
    width: number,
    height: number,
  ): Promise<string | null> {
    if (this.detector) {
      try {
        const values = await this.detector.detect(source);
        const accepted = values.find(({ rawValue }) => this.accepts(rawValue));
        if (accepted) return accepted.rawValue;
      } catch {
        this.detector = null;
      }
    }
    if (!this.context) throw new Error("This browser cannot read camera images.");
    const scale = Math.min(1, 960 / Math.max(width, height));
    this.canvas.width = Math.max(1, Math.round(width * scale));
    this.canvas.height = Math.max(1, Math.round(height * scale));
    this.context.drawImage(source, 0, 0, this.canvas.width, this.canvas.height);
    const pixels = this.context.getImageData(0, 0, this.canvas.width, this.canvas.height);
    const { default: jsQR } = await import("jsqr");
    const decoded = jsQR(pixels.data, pixels.width, pixels.height, {
      inversionAttempts: "attemptBoth",
    });
    return decoded && this.accepts(decoded.data) ? decoded.data : null;
  }
  private async tick(time: number, generation: number): Promise<void> {
    if (!this.active || this.scanning || generation !== this.generation) return;
    if (this.video.readyState >= 2 && time - this.lastScan > 180) {
      this.lastScan = time;
      try {
        const code = await this.decode(
          this.video,
          this.video.videoWidth,
          this.video.videoHeight,
        );
        if (code && this.active && generation === this.generation) {
          this.finish(code);
          return;
        }
      } catch (failure) {
        if (this.active) this.error.textContent = message(failure);
      }
    }
    if (this.active && generation === this.generation)
      this.frame = requestAnimationFrame((next) => void this.tick(next, generation));
  }
  private async scanImage(file: File): Promise<void> {
    const generation = this.generation;
    try {
      const bitmap = await createImageBitmap(file);
      let code: string | null;
      try {
        code = await this.decode(bitmap, bitmap.width, bitmap.height);
      } finally {
        bitmap.close();
      }
      if (!this.active || generation !== this.generation) return;
      if (code) this.finish(code);
      else this.error.textContent = "No matching Groundspark QR code was found in this image.";
    } catch (failure) {
      if (this.active) this.error.textContent = message(failure);
    }
  }
  private finish(code: string): void {
    if (this.scanning) return;
    this.scanning = true;
    this.generation += 1;
    this.stopCamera();
    this.onCode(code);
  }
  private stopCamera(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    this.video.srcObject = null;
  }
  public dispose(): void {
    this.active = false;
    this.generation += 1;
    this.stopCamera();
    this.canvas.width = this.canvas.height = 0;
  }
}
